import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  BookOpen,
  Clock,
  Star,
  ChevronRight,
  Search,
  Filter,
  TrendingUp,
  Loader2,
  AlertCircle,
  Crown,
  Calendar,
  Package
} from 'lucide-react'
import { contentApi, CurriculumSummary } from '@/api/content'
import { subscriptionApi } from '@/api/subscription'

// 학습 시간을 시간 단위로 변환
function formatDuration(minutes: number | null): string {
  if (!minutes) return '-'
  if (minutes < 60) return `${minutes}분`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`
}

// 레벨 결정 (임시 로직 - 추후 백엔드에서 결정)
function getLevel(curriculum: CurriculumSummary): string {
  const title = curriculum.title.toLowerCase()
  if (title.includes('기초') || title.includes('입문')) return '기초'
  if (title.includes('고급') || title.includes('심화')) return '고급'
  return '중급'
}

// 카테고리 결정 (임시 로직)
function getCategory(curriculum: CurriculumSummary): string {
  const title = curriculum.title
  if (title.includes('중학교') || title.includes('중등')) return '중등수학'
  if (title.includes('고등학교') || title.includes('고등')) return '고등수학'
  if (title.includes('수능')) return '수능수학'
  return '기타'
}

const CATEGORIES = ['전체', '중등수학', '고등수학', '수능수학', '기타']

// 이모지 매핑 (임시)
const CATEGORY_EMOJI: Record<string, string> = {
  '중등수학': '📐',
  '고등수학': '📊',
  '수능수학': '📈',
  '기타': '📚'
}

export function LearnPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('전체')

  // API로 커리큘럼 데이터 가져오기
  const { 
    data: curricula = [], 
    isLoading, 
    error 
  } = useQuery({
    queryKey: ['curricula'],
    queryFn: contentApi.getCurricula,
    staleTime: 5 * 60 * 1000, // 5분
  })

  // 내 구독 정보 가져오기
  const { data: subscriptionData } = useQuery({
    queryKey: ['my-subscriptions'],
    queryFn: () => subscriptionApi.getMySubscriptions(),
    staleTime: 60 * 1000, // 1분
  })

  const filteredCurricula = curricula.filter(curriculum => {
    const category = getCategory(curriculum)
    const matchesSearch = curriculum.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (curriculum.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false)
    const matchesCategory = selectedCategory === '전체' || category === selectedCategory
    return matchesSearch && matchesCategory
  })

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-4" />
        <p className="text-gray-600">콘텐츠를 불러오는 중...</p>
      </div>
    )
  }

  // 에러 상태
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-gray-900 font-medium mb-2">데이터를 불러올 수 없습니다</p>
        <p className="text-gray-500 text-sm">잠시 후 다시 시도해주세요</p>
      </div>
    )
  }

  // 활성 구독 필터링
  const activeSubscriptions = subscriptionData?.filter(sub => sub.status === 'active') || []

  return (
    <div className="space-y-8">
      {/* 내 구독 정보 */}
      {activeSubscriptions.length > 0 && (
        <section className="bg-linear-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Crown className="w-6 h-6" />
            </div>
            <div>
              <h2 className="font-bold text-lg">내 구독</h2>
              <p className="text-white/80 text-sm">{activeSubscriptions.length}개 상품 구독 중</p>
            </div>
          </div>
          <div className="space-y-3">
            {activeSubscriptions.map((sub) => (
              <div
                key={sub.id}
                className="bg-white/10 backdrop-blur-xs rounded-xl p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-white/80" />
                  <div>
                    <p className="font-semibold">{sub.productName}</p>
                    <p className="text-sm text-white/70">
                      {sub.subscriptionType === 'yearly' ? '연간' : 
                       sub.subscriptionType === 'monthly' ? '월간' : 
                       sub.subscriptionType === 'one_time' ? '평생' : sub.subscriptionType} 구독
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-sm text-white/80">
                    <Calendar className="w-4 h-4" />
                    {sub.validUntil 
                      ? `${new Date(sub.validUntil).toLocaleDateString('ko-KR')}까지`
                      : '무제한'
                    }
                  </div>
                  {sub.curriculumTitle && (
                    <Link
                      to={`/learn/curriculum/${sub.curriculumContentId}`}
                      className="text-sm text-white underline hover:text-white/80 mt-1 inline-block"
                    >
                      {sub.curriculumTitle} →
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">학습 시작하기</h1>
          <p className="text-gray-600 mt-1">나에게 맞는 수학 강좌를 선택하세요</p>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="강좌 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      {/* Category Filter */}
      <section>
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <Filter className="w-4 h-4 text-gray-500 shrink-0" />
          {CATEGORIES.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors
                ${selectedCategory === category 
                  ? 'bg-primary-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Curriculum List */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary-600" />
          커리큘럼 목록
          <span className="text-sm font-normal text-gray-500">({filteredCurricula.length}개)</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCurricula.map((curriculum) => {
            const category = getCategory(curriculum)
            const level = getLevel(curriculum)
            const emoji = CATEGORY_EMOJI[category] || '📚'
            
            return (
              <Link
                key={curriculum.id}
                to={`/learn/curriculum/${curriculum.id}`}
                className="card overflow-hidden hover:shadow-lg transition-all group"
              >
                {/* Thumbnail Area */}
                <div className="h-32 bg-linear-to-br from-primary-100 to-primary-50 flex items-center justify-center relative">
                  <span className="text-5xl">{emoji}</span>
                </div>
                
                {/* Content */}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                      ${level === '기초' ? 'bg-green-100 text-green-700' : 
                        level === '중급' ? 'bg-yellow-100 text-yellow-700' : 
                        'bg-red-100 text-red-700'}`}>
                      {level}
                    </span>
                    <span className="text-xs text-gray-500">{category}</span>
                  </div>
                  
                  <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                    {curriculum.title}
                  </h3>
                  <p className="text-sm text-gray-500 line-clamp-2 mb-4">
                    {curriculum.description || curriculum.shortDescription || '수학의 기초를 다지는 커리큘럼입니다.'}
                  </p>
                  
                  {/* Stats */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-3.5 h-3.5" />
                        {curriculum.courseCount}개 코스
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDuration(curriculum.estimatedDurationMinutes)}
                      </span>
                    </div>
                    {curriculum.starRating && (
                      <span className="flex items-center gap-1 text-yellow-500">
                        <Star className="w-3.5 h-3.5 fill-current" />
                        {curriculum.starRating}
                      </span>
                    )}
                  </div>
                  
                  {/* Lesson Count */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">총 {curriculum.totalLessonCount}개 강의</span>
                      <ChevronRight className="w-4 h-4 text-primary-600" />
                    </div>
                  </div>
                </div>
              </Link>
            )
          })}
        </div>
      </section>

      {/* Empty State */}
      {filteredCurricula.length === 0 && (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Search className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {curricula.length === 0 ? '등록된 커리큘럼이 없습니다' : '검색 결과가 없습니다'}
          </h3>
          <p className="text-gray-500">
            {curricula.length === 0 
              ? '콘텐츠가 준비 중입니다. 잠시 후 다시 확인해주세요.' 
              : '다른 검색어나 카테고리를 선택해보세요'
            }
          </p>
        </div>
      )}

      {/* Quick Stats */}
      <section className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-primary-600" />
          콘텐츠 현황
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-primary-50 rounded-xl">
            <div className="text-3xl font-bold text-primary-600">{curricula.length}</div>
            <div className="text-sm text-gray-600 mt-1">커리큘럼</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-xl">
            <div className="text-3xl font-bold text-green-600">
              {curricula.reduce((sum, c) => sum + c.courseCount, 0)}
            </div>
            <div className="text-sm text-gray-600 mt-1">코스</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-xl">
            <div className="text-3xl font-bold text-yellow-600">
              {curricula.reduce((sum, c) => sum + c.totalLessonCount, 0)}
            </div>
            <div className="text-sm text-gray-600 mt-1">총 강의</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-xl">
            <div className="text-3xl font-bold text-purple-600">
              {formatDuration(curricula.reduce((sum, c) => sum + (c.estimatedDurationMinutes || 0), 0))}
            </div>
            <div className="text-sm text-gray-600 mt-1">총 학습시간</div>
          </div>
        </div>
      </section>
    </div>
  )
}
